/**
 * Auth context + provider.
 *
 * Holds the current user (UserDetail | null), exposes login / logout /
 * refreshUser, and listens for the `hy:auth-expired` event the API
 * client dispatches on 401 so the SPA can react centrally.
 *
 * The JWT itself lives in localStorage (key `hy.auth.token`) — see
 * src/api/client.ts. This provider doesn't navigate; route guards and
 * pages handle that. Keeps the context easy to test and reason about.
 */

import { createContext, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import {
  changePasswordRequest,
  fetchMe,
  loginRequest,
  type ChangePasswordRequest,
  type LoginRequest,
  type TokenResponse,
  type UserDetail,
} from '../api/auth';
import { ApiError, AUTH_EXPIRED_EVENT, AUTH_TOKEN_KEY } from '../api/client';
import { clearAnnouncementSession } from '../data/announcements';

const ME_QUERY_KEY = ['auth', 'me'] as const;

/**
 * Drop every cached query EXCEPT /me.
 *
 * `queryClient.clear()` looks like the obvious call here, and it is a
 * trap: it removes the /me query OBJECT, while the mounted observer in
 * this provider stays bound to the removed one. A `setQueryData`
 * afterwards writes to a freshly created object that nothing is
 * listening to, so `user` keeps its old value until something else
 * happens to re-render.
 *
 * That is not cosmetic. On sign-out it left the app showing a signed-in
 * header on a page whose every request 401s; on sign-in it produced a
 * render with `user: null` and `isReady: true`, which route guards read
 * as "not signed in" and bounced people back to /login.
 *
 * Removing everything else and writing /me through its live observer
 * keeps the security intent — no previous person's rows survive — while
 * leaving auth state reactive.
 */
function clearExceptMe(queryClient: QueryClient): void {
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== ME_QUERY_KEY[0],
  });
}

export interface AuthContextValue {
  /** The authenticated user, or null if not signed in. */
  user: UserDetail | null;
  /** True after the initial /me probe resolves (success or otherwise). */
  isReady: boolean;
  /** Convenience flag, derived from `user`. */
  isAuthenticated: boolean;
  /** POST /api/auth/login. Returns the token response so callers can read `must_change_password`. */
  login: (payload: LoginRequest) => Promise<TokenResponse>;
  /** Clear the token + cached user. Does not navigate. */
  logout: () => void;
  /** Force a fresh /api/auth/me fetch. Useful after profile changes. */
  refreshUser: () => Promise<void>;
  /** POST /api/auth/change-password. Updates the stored token + user on success. */
  changePassword: (payload: ChangePasswordRequest) => Promise<TokenResponse>;
}

// eslint-disable-next-line react-refresh/only-export-components -- consumed by the useAuth hook in a sibling file
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();

  // `enabled` follows whether a token is stored. The query refires
  // whenever we set/clear the token via setQueryData side effects.
  const meQuery = useQuery<UserDetail | null>({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      try {
        return await fetchMe();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    enabled: typeof window !== 'undefined' && !!localStorage.getItem(AUTH_TOKEN_KEY),
    staleTime: 60_000,
    retry: false,
  });

  // Listen for the API client's "your token died" event and clear
  // local state. Components reading useAuth will re-render with
  // user=null; route guards then redirect.
  useEffect(() => {
    const onExpired = () => {
      queryClient.setQueryData(ME_QUERY_KEY, null);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, [queryClient]);

  const login = useCallback(
    async (payload: LoginRequest): Promise<TokenResponse> => {
      const token = await loginRequest(payload);
      localStorage.setItem(AUTH_TOKEN_KEY, token.access_token);

      // Fetch /me BEFORE clearing the cache, not after.
      //
      // `clear()` removes the /me entry, and the observer immediately
      // re-creates and refetches it on its own. Awaiting a second fetch
      // after that left a window where the cache was empty but a token
      // existed — and a render in that window sees `user: null` with
      // `isReady: true`, which is exactly what RequireAuth treats as
      // "not signed in". The caller navigates, the guard bounces them
      // straight back to /login, and by the time /me resolves they are
      // already sitting on the login page, signed in, with nothing left
      // to move them off it.
      //
      // Fetching first means clear() and setQueryData are adjacent
      // synchronous calls with no await between them, so React cannot
      // render the empty state at all.
      const user = await fetchMe();

      // Drop anything cached for whoever was here before. Without this a
      // second person signing in on the same browser is served the first
      // person's rows until each query happens to go stale.
      clearExceptMe(queryClient);
      // A fresh sign-in is a fresh session for announcements. sessionStorage
      // is scoped to the TAB, so it outlives a logout and would otherwise
      // suppress the banner for someone who has genuinely just signed in.
      clearAnnouncementSession();
      queryClient.setQueryData(ME_QUERY_KEY, user);
      return token;
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    // Everything, not just /me. This app caches accounts, visit notes and
    // depletions — none of which should survive into the next person's
    // session on a shared machine.
    clearExceptMe(queryClient);
    clearAnnouncementSession();
    queryClient.setQueryData(ME_QUERY_KEY, null);
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }, [queryClient]);

  const changePassword = useCallback(
    async (payload: ChangePasswordRequest): Promise<TokenResponse> => {
      const token = await changePasswordRequest(payload);
      localStorage.setItem(AUTH_TOKEN_KEY, token.access_token);
      const user = await fetchMe();
      queryClient.setQueryData(ME_QUERY_KEY, user);
      return token;
    },
    [queryClient],
  );

  // If there is no token at mount, the query is disabled and never
  // resolves. Treat that as "ready, not signed in".
  const isReady = useMemo(() => {
    if (typeof window === 'undefined') return false;
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) return true;
    return !meQuery.isLoading;
  }, [meQuery.isLoading]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      isReady,
      isAuthenticated: !!meQuery.data,
      login,
      logout,
      refreshUser,
      changePassword,
    }),
    [meQuery.data, isReady, login, logout, refreshUser, changePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
